"use client";

import { useEffect, useState } from "react";

import {
  fetchEnabledModulesForWorkspace,
  fetchStoryModuleEnabled,
} from "@/server-actions/modules/actions";
import { fetchDemCogModuleEnabled } from "@/server-actions/dem-cog/module-check";
import { isUserCommunityLeader } from "@/server-actions/users/communities/action";
import { isModuleEnabledByKey } from "@/lib/modules/display-names";
import {
  MANAGEMENT_MODULE_ID,
  SCRIPT_GENERATOR_MODULE_ID,
} from "@/lib/rubrics/constants";
import { ON_THE_FLY_MODULE_ID } from "@/lib/on-the-fly";

/**
 * Everything the menus need to know about this studio beyond the session
 * itself: which optional modules it runs, and whether this member leads a
 * community.
 *
 * The shape returned here is exactly what `createNavContext` takes, so the
 * header and the ⌘K index answer the manifest's `requires` names from the same
 * definitions — the client reading server actions, the server reading Prisma.
 */
export function useStudioNavData(session) {
  const workspaceId = session?.user?.workspaceId;
  const userId = session?.user?.id;

  const [enabledModules, setEnabledModules] = useState([]);
  const [isCommunityLeader, setIsCommunityLeader] = useState(false);
  const [storyModuleEnabled, setStoryModuleEnabled] = useState(false);
  const [demCogModuleEnabled, setDemCogModuleEnabled] = useState(false);

  useEffect(() => {
    if (!userId) return;
    isUserCommunityLeader(userId)
      .then(({ isLeader }) => setIsCommunityLeader(isLeader))
      .catch(console.error);
  }, [userId]);

  useEffect(() => {
    if (!workspaceId) return;

    fetchStoryModuleEnabled(workspaceId)
      .then((enabled) => setStoryModuleEnabled(!!enabled))
      .catch(console.error);

    fetchDemCogModuleEnabled(workspaceId)
      .then((enabled) => setDemCogModuleEnabled(!!enabled))
      .catch(console.error);

    fetchEnabledModulesForWorkspace(workspaceId)
      .then((modules) => setEnabledModules(modules || []))
      .catch((error) => {
        // Transient network failures (navigation aborts, dev-server restarts)
        // land here — keep the last known state instead of hiding nav items.
        console.warn("Skipping enabled-modules refresh:", error?.message);
      });
  }, [workspaceId]);

  const hasModule = (moduleId) =>
    enabledModules.some((module) => module?.id === moduleId);

  return {
    enabledModules,
    isCommunityLeader,
    modules: {
      stories: storyModuleEnabled,
      dealClinic: demCogModuleEnabled,
      management: hasModule(MANAGEMENT_MODULE_ID),
      conversationAnalysis: isModuleEnabledByKey(
        enabledModules,
        "CONVERSATION_ANALYSIS",
      ),
      onTheFly: enabledModules.some(
        (module) =>
          module?.id === ON_THE_FLY_MODULE_ID ||
          module?.href === "/simulator/on-the-fly",
      ),
      scriptGenerator: hasModule(SCRIPT_GENERATOR_MODULE_ID),
    },
  };
}
